import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  Calendar,
  User,
  Star,
  X,
  Play,
  FileText,
  File,
  Lock,
  Unlock,
  Plus, // Icon for "Add Resource"
  Trash2,
  Save,
  Eye, // Keep Eye icon import if it's used elsewhere, otherwise remove
} from "lucide-react";
import Header from "../Components/Header";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
}
 from "firebase/storage";
import { db, functions, storage } from "../firebase/config";
import SingleRoadmapAdminViewSkeleton from "../Skeleton/SingleRoadmapAdminViewSkeleton";
import {
  getLevelStyling,
  getStatusBadgeColor,
  getStatusColor,
  getStatusIcon,
  renderStars,
} from "../utils/roadmapStylingHelper";
import {httpsCallable } from "firebase/functions";


const SingleRoadmapAdminView = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [roadmapData, setRoadmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [coachFeedbackInput, setCoachFeedbackInput] = useState("");

  // State for resources currently associated with the session (fetched from Firestore)
  const [currentSessionResources, setCurrentSessionResources] = useState([]);
  // State for new files selected by the admin, pending upload
  const [filesToUploadStaging, setFilesToUploadStaging] = useState([]);
  // New state for Google Drive link input
  const [googleDriveLink, setGoogleDriveLink] = useState("");
  // New state for selected new resource type (file or drive link)
  const [newResourceTypeSelection, setNewResourceTypeSelection] = useState("file");

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const initialUnlockProcessed = useRef(false);
  const globalRoadmapStructure = useRef(null);
  const fileInputRef = useRef(null);

  const getNextSessionToUnlock = useCallback(
    (currentSessionId, allGlobalItemsFlat, studentRoadmapProgressMap) => {
      const currentIndex = allGlobalItemsFlat.findIndex(
        (item) => item.id === currentSessionId
      );
      if (
        currentIndex === -1 ||
        currentIndex >= allGlobalItemsFlat.length - 1
      ) {
        return null;
      }

      const nextGlobalItem = allGlobalItemsFlat[currentIndex + 1];
      const nextStudentProgress = studentRoadmapProgressMap[nextGlobalItem.id];

      if (
        !nextStudentProgress ||
        (nextStudentProgress.status === "locked" &&
          !nextStudentProgress.unlockedByAdmin)
      ) {
        return nextGlobalItem;
      }
      return null;
    },
    []
  );

  useEffect(() => {
    if (!studentId) return;

    setLoading(true);

    let unsubscribeStudent = () => {};
    let unsubscribeProgress = () => {};

    const setupListeners = async () => {
      const studentDocRef = doc(db, "users", studentId);
      unsubscribeStudent = onSnapshot(studentDocRef, (docSnap) => {
        if (docSnap.exists()) {
          setStudent({ id: docSnap.id, ...docSnap.data() });
        } else {
          console.error("Student not found!");
          setStudent(null);
        }
      });

      if (!globalRoadmapStructure.current) {
        const levelsCollectionRef = collection(db, "roadmapLevels");
        const levelsQuery = query(levelsCollectionRef, orderBy("order"));
        const levelsSnapshot = await getDocs(levelsQuery);

        const fetchedGlobalLevels = [];
        const fetchedGlobalRoadmapItemsFlat = [];

        for (const levelDoc of levelsSnapshot.docs) {
          const levelData = { id: levelDoc.id, ...levelDoc.data() };
          const dynamicStyles = getLevelStyling(levelData.order);

          const sessionsCollectionRef = collection(
            db,
            `roadmapLevels/${levelDoc.id}/roadmap_sessions`
          );
          const sessionsQuery = query(sessionsCollectionRef, orderBy("order"));
          const sessionsSnapshot = await getDocs(sessionsQuery);

          const levelSessions = [];
          sessionsSnapshot.forEach((sessionDoc) => {
            const sessionData = {
              id: sessionDoc.id,
              ...sessionDoc.data(),
              levelId: levelDoc.id,
            };
            levelSessions.push(sessionData);
            fetchedGlobalRoadmapItemsFlat.push(sessionData);
          });
          fetchedGlobalLevels.push({
            ...levelData,
            ...dynamicStyles,
            items: levelSessions,
          });
        }
        fetchedGlobalLevels.sort((a, b) => (a.order || 0) - (b.order || 0));
        globalRoadmapStructure.current = {
          levels: fetchedGlobalLevels,
          flatItems: fetchedGlobalRoadmapItemsFlat,
        };
      }

      const studentProgressCollectionRef = collection(
        db,
        `users/${studentId}/roadmapProgress`
      );
      unsubscribeProgress = onSnapshot(
        studentProgressCollectionRef,
        async (progressSnapshot) => {
          const studentProgressMap = {};
          progressSnapshot.forEach((progressDoc) => {
            studentProgressMap[progressDoc.id] = progressDoc.data();
          });

          if (!globalRoadmapStructure.current) {
            console.warn(
              "Global roadmap structure not yet loaded. Skipping progress merge."
            );
            return;
          }

          const mergedRoadmap = {
            student: student,
            levels: globalRoadmapStructure.current.levels.map((level) => {
              let levelCompletedItems = 0;
              let levelInProgressItems = 0;
              let levelTotalItems = level.items.length;

              const itemsWithProgress = level.items.map((globalItem) => {
                const studentItem = studentProgressMap[globalItem.id];
                let status = "locked";
                let resources = [];

                if (studentItem) {
                  status = studentItem.status;
                  if (studentItem.status === "completed") levelCompletedItems++;
                  if (studentItem.status === "in-progress")
                    levelInProgressItems++;
                  resources = studentItem.resources || [];
                } else {
                  resources = [];
                }

                return {
                  ...globalItem,
                  status: status,
                  coachFeedback: studentItem?.coachFeedback || "",
                  selfEvaluation: studentItem?.selfEvaluation || "",
                  rating: studentItem?.rating || 0,
                  unlockedByAdmin: studentItem?.unlockedByAdmin || false,
                  resources: resources,
                };
              });

              const levelProgress =
                levelTotalItems > 0
                  ? Math.round((levelCompletedItems / levelTotalItems) * 100)
                  : 0;

              return {
                ...level,
                items: itemsWithProgress,
                progress: levelProgress,
                completed: levelCompletedItems,
                total: levelTotalItems,
              };
            }),
          };

          if (!initialUnlockProcessed.current) {
            let hasUnlockedItems = Object.values(studentProgressMap).some(
              (p) => p.status !== "locked"
            );

            if (
              !hasUnlockedItems &&
              globalRoadmapStructure.current.flatItems.length > 0
            ) {
              const firstSessionRef =
                globalRoadmapStructure.current.flatItems[0];
              console.log(
                "No unlocked items found, attempting to unlock first session:",
                firstSessionRef.id
              );
              const sessionProgressRef = doc(
                db,
                `users/${studentId}/roadmapProgress/${firstSessionRef.id}`
              );
              await setDoc(
                sessionProgressRef,
                {
                  sessionId: firstSessionRef.id,
                  status: "not-started",
                  completionDate: null,
                  coachFeedback: "",
                  selfEvaluation: "",
                  rating: 0,
                  unlockedByAdmin: false,
                  resources: [],
                },
                { merge: true }
              );
              initialUnlockProcessed.current = true;
            } else if (hasUnlockedItems) {
              initialUnlockProcessed.current = true;
            }
          }

          let totalCompletedOverall = 0;
          let totalItemsOverall = 0;
          mergedRoadmap.levels.forEach((level) => {
            level.items.forEach((item) => {
              totalItemsOverall++;
              if (item.status === "completed") {
                totalCompletedOverall++;
              }
            });
          });
          mergedRoadmap.stats = {
            totalItems: totalItemsOverall,
            inProgress: Object.values(studentProgressMap).filter(
              (p) => p.status === "in-progress"
            ).length,
            globalProgress:
              totalItemsOverall > 0
                ? Math.round((totalCompletedOverall / totalItemsOverall) * 100)
                : 0,
          };

          setRoadmapData(mergedRoadmap);
          setLoading(false);
        },
        (error) => {
          console.error("Error fetching student progress snapshot:", error);
          setLoading(false);
        }
      );
    };

    setupListeners();

    return () => {
      unsubscribeStudent();
      unsubscribeProgress();
    };
  }, [studentId, getNextSessionToUnlock]);

  const handleUpdateItemStatus = async (
    itemToUpdate,
    newStatus,
    feedback = "",
    isByAdmin = false,
    rating = 0,
    selfEvaluation = ""
  ) => {
    try {
      const itemProgressRef = doc(
        db,
        `users/${studentId}/roadmapProgress/${itemToUpdate.id}`
      );
      const updateData = {
        sessionId: itemToUpdate.id,
        status: newStatus,
        completionDate:
          newStatus === "completed"
            ? new Date()
            : newStatus === "locked" ||
              newStatus === "not-started" ||
              newStatus === "unlocked"
            ? null
            : itemToUpdate.completionDate,
        coachFeedback: feedback,
        rating: rating,
        selfEvaluation: selfEvaluation,
        unlockedByAdmin: isByAdmin,
      };

      await setDoc(itemProgressRef, updateData, { merge: true });

      console.log(`Item ${itemToUpdate.title} updated to ${newStatus}`);

      if (
        newStatus === "completed" &&
        roadmapData &&
        globalRoadmapStructure.current
      ) {
        const allGlobalItemsFlat = globalRoadmapStructure.current.flatItems;

        const currentStudentProgressSnapshot = await getDocs(
          collection(db, `users/${studentId}/roadmapProgress`)
        );
        const currentStudentProgressMap = {};
        currentStudentProgressSnapshot.forEach((doc) => {
          currentStudentProgressMap[doc.id] = doc.data();
        });

        const nextGlobalItem = getNextSessionToUnlock(
          itemToUpdate.id,
          allGlobalItemsFlat,
          currentStudentProgressMap
        );

        if (nextGlobalItem) {
          console.log("Auto-unlocking next session:", nextGlobalItem.title);
          const nextSessionProgressRef = doc(
            db,
            `users/${studentId}/roadmapProgress/${nextGlobalItem.id}`
          );
          await setDoc(
            nextSessionProgressRef,
            {
              sessionId: nextGlobalItem.id,
              status: "not-started",
              completionDate: null,
              coachFeedback: "",
              selfEvaluation: "",
              rating: 0,
              unlockedByAdmin: false,
              resources: [],
            },
            { merge: true }
          );
        }
      }

      // Only close modal if the update was initiated from the modal's save/cancel
      // If status is updated from the card, the modal might not be open.
      if (selectedItem && selectedItem.id === itemToUpdate.id) {
        setSelectedItem(null);
        setCoachFeedbackInput("");
        setCurrentSessionResources([]); // Clear resources state on status update
        setFilesToUploadStaging([]); // Clear staging files
        setGoogleDriveLink(""); // Clear Google Drive link
        setNewResourceTypeSelection("file"); // Reset resource type selection
        setSaveError(null); // Clear any save error
      }
      alert("Status updated successfully!"); // Success toast for status change
    } catch (error) {
      console.error("Error updating item status:", error);
      setSaveError(`Failed to update status: ${error.message}`);
    }
  };

  const handleAdminUnlock = async (item) => {
    if (
      window.confirm(
        `Are you sure you want to unlock "${item.title}" for this student?`
      )
    ) {
      await handleUpdateItemStatus(
        item,
        "not-started",
        item.coachFeedback,
        true,
        item.rating,
        item.selfEvaluation
      );
    }
  };

  const handleOpenModal = (item) => {
    if (item.status === "locked") {
      return;
    }
    setSelectedItem(item);
    setCoachFeedbackInput(item.coachFeedback || "");
    // Initialize currentSessionResources with existing resources (deep copy)
    setCurrentSessionResources(
      item.resources ? item.resources.map((res) => ({ ...res })) : []
    );
    setFilesToUploadStaging([]); // Clear staging area for new files
    setGoogleDriveLink(""); // Clear Google Drive link input
    setNewResourceTypeSelection("file"); // Reset resource type selection
    setSaveError(null); // Clear previous errors
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the actual file input element
    }
  };

  // Handles adding files to the staging area (filesToUploadStaging)
  const handleAddFiles = (e) => {
    if (e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).map((file) => ({
        file: file,
        name: file.name,
        type: file.type.startsWith("image/")
          ? "image"
          : file.type.startsWith("video/")
          ? "video"
          : "document",
        sourceType: "file", // Indicate it's a direct file upload
      }));
      setFilesToUploadStaging((prevFiles) => [...prevFiles, ...newFiles]);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear the input after selection
      }
    }
  };

  // Handles adding a Google Drive link to the staging area
  const handleAddGoogleDriveLink = () => {
    if (googleDriveLink.trim()) {
      // Basic validation for a Google Drive link
      const driveRegex = /(https?:\/\/(?:www\.)?drive\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+))|(https?:\/\/(?:www\.)?docs\.google\.com\/(?:document|spreadsheet|presentation)\/d\/([a-zA-Z0-9_-]+))/;
      const match = googleDriveLink.match(driveRegex);

      if (match) {
        const fileId = match[2] || match[4]; // Extract file ID
        if (fileId) {
          setFilesToUploadStaging((prevFiles) => [
            ...prevFiles,
            {
              name: `Google Drive Link (${fileId.substring(0, 8)}...)`, // Display a truncated ID
              type: "link", // Or 'document', 'video' based on actual content if known
              url: googleDriveLink.trim(), // Store the raw link for the Cloud Function
              sourceType: "google-drive", // Indicate it's a Google Drive link
              fileId: fileId, // Store the extracted file ID
            },
          ]);
          setGoogleDriveLink(""); // Clear the input field
        } else {
          setSaveError("Could not extract file ID from Google Drive link. Please check the URL format.");
        }
      } else {
        setSaveError("Invalid Google Drive link format. Please use a valid Google Drive URL.");
      }
    }
  };

  // Handles removing a file from the filesToUploadStaging
  const handleRemoveStagedFile = (indexToRemove) => {
    setFilesToUploadStaging((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  // Handles removing an existing resource from currentSessionResources
  const handleRemoveCurrentResource = (urlToRemove) => {
    setCurrentSessionResources((prevResources) =>
      prevResources.filter((res) => res.url !== urlToRemove)
    );
  };

  // Main function to handle saving all changes (feedback + resources)
  const handleSaveChanges = async () => {
    if (!selectedItem || !studentId) return;

    setIsSavingChanges(true);
    setSaveError(null);

    try {
      const itemProgressRef = doc(
        db,
        `users/${studentId}/roadmapProgress/${selectedItem.id}`
      );

      const directFiles = filesToUploadStaging.filter(f => f.sourceType === "file");
      const driveLinks = filesToUploadStaging.filter(f => f.sourceType === "google-drive");

      // 1. Upload new direct files from staging area
      const uploadedResourcePromises = directFiles.map(async (res) => {
        const fileExtension = res.name.split(".").pop();
        const uniqueFileName = `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}.${fileExtension}`;
        const storageRef = ref(
          storage,
          `users/${studentId}/roadmap_sessions_resources/${selectedItem.id}/${uniqueFileName}`
        );
        const uploadTask = uploadBytesResumable(storageRef, res.file);

        return new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              // Progress can be shown here if needed, but for multiple files, it's complex
            },
            (error) => {
              console.error("Upload failed for file:", res.name, error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                // Ensure content-disposition for proper download behavior
                const finalDownloadURL = `${downloadURL}&content-disposition=attachment; filename="${encodeURIComponent(
                  res.name
                )}"`;
                resolve({
                  name: res.name,
                  type: res.type, // Use the detected type (image, video, document)
                  url: finalDownloadURL,
                });
              } catch (error) {
                console.error(
                  "Error getting download URL for file:",
                  res.name,
                  error
                );
                reject(error);
              }
            }
          );
        });
      });

      // 2. Process Google Drive links via Cloud Function
      const driveLinkProcessingPromises = driveLinks.map(async (link) => {
        try {
          const processDriveLinkFunction =  httpsCallable(functions, 'processDriveLink');
          const result = await processDriveLinkFunction({
            fileId: link.fileId,
            studentId: studentId,
            sessionId: selectedItem.id,
            originalFileName: link.name 
          });
          return result.data; 

        } catch (error) {
          console.error("Error processing Google Drive link:", link.url, error);
          setSaveError(`Failed to process Google Drive link: ${link.name}. Error: ${error.message}`);
          return null; 
        }
      });

      const newlyUploadedResources = (await Promise.all(uploadedResourcePromises)).filter(Boolean); // Filter out nulls
      const processedDriveResources = (await Promise.all(driveLinkProcessingPromises)).filter(Boolean); // Filter out nulls

      // Combine existing resources (those remaining in currentSessionResources) with newly uploaded ones
      const finalResourcesForFirestore = [
        ...currentSessionResources,
        ...newlyUploadedResources,
        ...processedDriveResources,
      ];

      // 3. Identify resources to delete from Storage (original resources vs. final list)
      const originalResourceUrls = new Set(
        selectedItem.resources?.map((res) => res.url) || []
      );
      const finalResourceUrls = new Set(
        finalResourcesForFirestore.map((res) => res.url)
      );

      const resourcesToDelete = Array.from(originalResourceUrls).filter(
        (url) => !finalResourceUrls.has(url)
      );

      const deletePromises = resourcesToDelete.map(async (url) => {
        try {
          // Only attempt to delete if it's a GCS URL (contains /o/ and ?alt=media)
          if (url.includes("/o/") && url.includes("?alt=media")) {
            const storagePath = url
              .split("&content-disposition")[0] // Remove content-disposition if present
              .split("?alt=media&token=")[0] // Remove token if present
              .split("/o/")[1]; // Get path after /o/
            const storageRef = ref(storage, storagePath);
            await deleteObject(storageRef);
            console.log("File deleted from Storage:", url);
          } else {
            console.warn("Skipping deletion for non-GCS URL:", url);
          }
        } catch (error) {
          console.error("Error deleting file from Storage:", url, error);
        }
      });
      await Promise.all(deletePromises);

      // 4. Update Firestore document with new feedback and final resources list
      await setDoc(
        itemProgressRef,
        {
          coachFeedback: coachFeedbackInput,
          resources: finalResourcesForFirestore,
        },
        { merge: true }
      );

      console.log("Changes saved successfully!");
      alert("Changes saved successfully!"); // Success toast
      setSelectedItem(null); // Close modal
      setCoachFeedbackInput("");
      setCurrentSessionResources([]); // Reset
      setFilesToUploadStaging([]); // Reset
      setGoogleDriveLink(""); // Reset Google Drive link input
      setNewResourceTypeSelection("file"); // Reset resource type selection
      setIsSavingChanges(false);
    } catch (error) {
      console.error("Error saving changes:", error);
      setSaveError(`Failed to save changes: ${error.message}`);
      setIsSavingChanges(false);
    }
  };

  if (loading || !roadmapData) {
    return <SingleRoadmapAdminViewSkeleton />;
  }

  return (
    <div className="text-white">
      <Header
        PageName={`${student?.name || "Student"}'s Roadmap Overview`}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <div className="bg-slate-900 space-y-6 p-4 rounded-lg border border-slate-700">
        {/* Hero Section */}
        <div className="relative rounded-lg bg-gradient-to-br p-6 from-purple-900 via-blue-900 to-indigo-900">
          <div className="flex start space-x-4">
            <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center">
              {student?.photoURL ? (
                <img
                  className="rounded-full w-full h-full object-cover"
                  src={student.photoURL}
                  alt={student.displayName}
                />
              ) : (
                <span className="text-lg font-bold">
                  {student?.displayName
                    ? student.displayName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                    : "U"}
                </span>
              )}
            </div>
            <div className="flex items-start flex-col">
              <h1 className="text-start text-2xl font-bold">
                {student?.displayName}'s {roadmapData?.student?.journey}
              </h1>
              <div className="mb-4">
                <p className="w-fit text-start px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm font-medium">
                  {roadmapData.stats.globalProgress} % Completed
                </p>
              </div>
            </div>
          </div>
          {/* Levels Section */}
          <div>
            <div className="flex items-center justify-between ">
              <h2 className="text-xl font-semibold">
                Learning Journey Progress
              </h2>
              <div className="text-right mb-2">
                <div className="text-3xl font-bold">
                  {roadmapData.stats.globalProgress}%
                </div>
                <p className="text-sm text-slate-300">
                  {roadmapData.levels.filter((l) => l.progress === 100).length}{" "}
                  of {roadmapData.levels.length} levels completed
                </p>
              </div>
            </div>

            {/* Level Progress Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {roadmapData.levels.map((level) => (
                <div
                  key={level.id}
                  className={`bg-[#203162] rounded-xl p-4 border border-white/10`}
                >
                  <div className="flex gap-2 mb-3">
                    {/* Using dynamic styling from getLevelStyling helper */}
                    <div
                      className={`w-8 h-8 ${level.iconBg} rounded-full flex items-center justify-center text-white font-bold text-sm mb-2`}
                    >
                      {level.order}
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-start font-semibold text-sm">
                        {level.name}
                      </h3>
                      <p className="text-start text-xs text-slate-300">
                        {level.subtitle}
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-300 mb-1">
                      <span>{level.completed} completed</span>
                      <span>{level.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div
                        className={`bg-gradient-to-r ${level.color} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${level.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <p className="text-start text-xs text-slate-400 leading-relaxed">
                    {level.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="h-fit space-y-6">
          {roadmapData.levels.map((level) => (
            <div key={level.id}>
              {/* Level Header */}
              <div className="flex justify-between mb-6">
                <div className="flex items-center space-x-4">
                  {/* Using dynamic styling from getLevelStyling helper */}
                  <div
                    className={`w-10 h-10 ${level.iconBg} rounded-full flex items-center justify-center text-white font-bold`}
                  >
                    {level.order}
                  </div>
                  <div>
                    <h2 className="text-start text-xl font-bold">
                      {level.name}
                    </h2>
                    <p className="text-slate-400">{level.subtitle}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">
                    {level.completed}/{level.total}
                  </div>
                  <p className="text-sm text-slate-400">items completed</p>
                </div>
              </div>

              {/* Items Grid */}
              {level.items.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {level.items.map((item, index) => (
                    <div
                      key={item.id}
                      className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-lg cursor-pointer ${getStatusColor(
                        item.status
                      )}`}
                      onClick={() => handleOpenModal(item)} 
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span
                            className={`text-xs px-2 py-1 ${getStatusBadgeColor(
                              item.status
                            )} rounded font-medium`}
                          >
                            <div className="flex items-center gap-1">
                              {getStatusIcon(item.status)}
                              {item.status.replace("-", " ")}
                            </div>
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>

                      <h3 className="text-start font-semibold text-white mb-2 text-sm leading-tight">
                        {item.title}
                      </h3>

                      {item.status === "locked" ? (
                        <div className="flex flex-col items-center justify-center h-20 text-slate-400">
                          <Lock className="w-8 h-8 mb-2" />
                          <p className="text-sm">Complete previous step</p>
                        </div>
                      ) : (
                        <>
                          {item.description && (
                            <p dangerouslySetInnerHTML={{__html: item.description}} className="text-start text-slate-400 text-xs mb-3 line-clamp-3" />
                          )}

                          <div className="flex items-center space-x-8 text-slate-400 text-sm mb-2">
                            {item.dueDate && (
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4" />
                                <span>Due: {item.dueDate}</span>
                              </div>
                            )}
                            {item.estimatedHours && (
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4" />
                                <span>{item.estimatedHours}h est.</span>
                              </div>
                            )}
                          </div>

                          <div className="mt-2 flex items-center justify-between">
                            <p className="flex items-start justify-start gap-1 text-sm text-start text-slate-400">
                              <File className="w-4 h-4" />{" "}
                              {item?.resources?.length || 0} Resources
                            </p>
                          </div>
                        </>
                      )}
                      {item.status === "locked" && !item.unlockedByAdmin && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAdminUnlock(item);
                            }}
                            className="flex items-center gap-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs transition-colors"
                          >
                            <Unlock className="w-3 h-3" /> Unlock for Student
                          </button>
                        </div>
                      )}
                      {/* Mark as Completed button directly on the card */}
                      {item.status !== "completed" && item.status !== "locked" && (
                          <div className="mt-3 pt-3 border-t border-slate-700 flex justify-end">
                              <button
                                  onClick={(e) => {
                                      e.stopPropagation(); // Prevent opening modal when clicking this button
                                      handleUpdateItemStatus(item, "completed", coachFeedbackInput, item.unlockedByAdmin, item.rating, item.selfEvaluation);
                                  }}
                                  className="px-3 py-1 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center gap-1 text-xs"
                              >
                                  <CheckCircle className="w-3 h-3" /> Mark as Completed
                              </button>
                          </div>
                      )}
                      {/* Removed View/Edit Details button */}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p>No items available for this level yet</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Modal */}
        {selectedItem && selectedItem.status !== "locked" && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl max-w-3xl w-full max-h-[90vh] scrollbar-thin overflow-y-auto text-white">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-start font-bold">
                    {selectedItem.title}
                  </h2>
                  <button
                    onClick={() => {
                      setSelectedItem(null);
                      setCoachFeedbackInput("");
                      setCurrentSessionResources([]);
                      setFilesToUploadStaging([]);
                      setGoogleDriveLink(""); // Clear Google Drive link
                      setNewResourceTypeSelection("file"); // Reset resource type selection
                      setSaveError(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                <div className="mb-4 flex items-start">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(
                      selectedItem.status
                    )}`}
                  >
                    {selectedItem.status.replace("-", " ")}
                  </span>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-start font-semibold mb-2">
                      Description
                    </h3>
                    <p dangerouslySetInnerHTML={{__html: selectedItem.description}} className="text-start text-slate-300" />
                  </div>

                  <div className="flex items-center space-x-8 text-slate-400">
                    {selectedItem.dueDate && (
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>Due: {selectedItem.dueDate}</span>
                      </div>
                    )}
                    {selectedItem.estimatedHours && (
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4" />
                        <span>
                          {selectedItem.estimatedHours} hours estimated
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Resource Management Section */}
                  <div>
                    <h3 className="text-start font-semibold mb-3">
                      Student's Current Resources (
                      {currentSessionResources.length})
                    </h3>
                    <div className="space-y-3 mb-4">
                      {currentSessionResources.length > 0 ? (
                        currentSessionResources.map((resource, index) => (
                          <div
                            key={resource.url || index}
                            className="flex justify-between p-3 bg-slate-700 rounded-lg"
                          >
                            <div className="flex gap-2">
                              <div className="w-fit">
                                {resource.type === "video" ? (
                                  <Play className="w-5 h-5 text-purple-400" />
                                ) : (
                                  <FileText className="w-5 h-5 text-purple-400" />
                                )}
                              </div>
                              <span className="text-slate-300 flex-1 truncate">
                                {resource.name}
                              </span>
                              <span className="text-xs px-2 py-1 bg-purple-900 text-purple-300 rounded">
                                {resource.type}
                              </span>
                            </div>
                            <button
                              onClick={() =>
                                handleRemoveCurrentResource(resource.url)
                              }
                              className="p-1 rounded-full text-red-400 hover:bg-red-900/20 transition-colors"
                              title="Remove resource"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-400 text-sm">
                          No resources attached for this student yet.
                        </p>
                      )}
                    </div>

                    {/* Add Resource Area */}
                    <div className="p-4 border border-slate-700 rounded-lg bg-slate-700/30">
                      <h4 className="text-start font-semibold text-slate-200 mb-3">
                        Add New Resource
                      </h4>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          Select Resource Type
                        </label>
                        <select
                          value={newResourceTypeSelection}
                          onChange={(e) => setNewResourceTypeSelection(e.target.value)}
                          className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                        >
                          <option value="file">Upload File</option>
                          <option value="google-drive">Google Drive Link</option>
                        </select>
                      </div>

                      {newResourceTypeSelection === "file" && (
                        <input
                          type="file"
                          multiple // Allow multiple file selection
                          ref={fileInputRef}
                          onChange={handleAddFiles}
                          className="block w-full text-sm text-slate-300
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-500 file:text-white
                            hover:file:bg-blue-600 cursor-pointer mb-3"
                        />
                      )}

                      {newResourceTypeSelection === "google-drive" && (
                        <div className="flex gap-2 mb-3">
                          <input
                            type="text"
                            placeholder="Paste Google Drive Link"
                            value={googleDriveLink}
                            onChange={(e) => {
                                setGoogleDriveLink(e.target.value);
                                setSaveError(null); // Clear error on input change
                            }}
                            className="flex-1 p-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400"
                          />
                          <button
                            onClick={handleAddGoogleDriveLink}
                            className="p-2 bg-purple-600 hover:bg-purple-700 rounded-md text-white"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                      )}

                      {/* Display newly selected files/links here */}
                      {filesToUploadStaging.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm text-slate-300">
                            Items to be added:
                          </p>
                          {filesToUploadStaging.map((item, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-slate-600 rounded-md"
                            >
                              <span className="text-sm text-slate-200 truncate">
                                {item.name} ({item.sourceType === "file" ? "File" : "Drive Link"})
                              </span>
                              <button
                                onClick={() => handleRemoveStagedFile(index)}
                                className="ml-2 p-1 rounded-full text-red-300 hover:bg-red-800/50 transition-colors"
                                title="Remove from staging"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Admin Feedback Section */}
                  <div className="bg-slate-700 border border-blue-400 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <User className="w-4 h-4 text-blue-400" />
                      <h3 className="text-start font-semibold text-blue-300">
                        Coach Feedback
                      </h3>
                    </div>
                    <textarea
                      className="w-full p-2 bg-slate-600 border border-slate-500 rounded-md text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      rows="3"
                      placeholder="Enter your feedback here..."
                      value={coachFeedbackInput}
                      onChange={(e) => setCoachFeedbackInput(e.target.value)}
                    ></textarea>
                  </div>

                  {/* Student Self Evaluation */}
                  <div className="bg-yellow-900/20 rounded-lg p-4 border border-yellow-700">
                    <h3 className="text-start font-semibold text-yellow-300 mb-3">
                      Self Evaluation
                    </h3>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex space-x-1">
                        {renderStars(selectedItem.rating)}
                      </div>
                      <span className="text-sm text-yellow-500">
                        ({selectedItem.rating}/5)
                      </span>
                    </div>
                    <p className="text-yellow-200 text-start">
                      {selectedItem.selfEvaluation ||
                        "No self evaluation provided."}
                    </p>
                  </div>

                  {/* Action Buttons in Modal */}
                  {saveError && (
                    <p className="text-red-400 text-sm text-center mb-3">
                      {saveError}
                    </p>
                  )}
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                    <button
                      onClick={() => {
                        setSelectedItem(null);
                        setCoachFeedbackInput("");
                        setCurrentSessionResources([]);
                        setFilesToUploadStaging([]);
                        setGoogleDriveLink(""); // Clear Google Drive link
                        setNewResourceTypeSelection("file"); // Reset resource type selection
                        setSaveError(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      className="px-4 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      disabled={isSavingChanges}
                      className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingChanges ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" /> Save Changes
                        </>
                      )}
                    </button>
                    {/* Removed Mark as Completed button from modal */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleRoadmapAdminView;
